import React from 'react';
import { SafeImg } from '../../SafeImg';
import { getWbButtonAttrs } from '../../sqs/WbButton';
import { Btn, H1, Eyebrow, E } from './_shared';

/* ════════════════════════════════════════════════════════════════════════
 * SERVICES — pricing variants (pricing-tabs · pricing-comparison-grid ·
 * pricing-dark-hero · pricing-highlighted-middle · pricing-membership)
 *
 * Iter 175c — Section Audit. Title · tagline · per-tier name/price/
 * description, every feature row, CTA labels, comparison-grid feature
 * names, aside text on the table — all inline-editable. Sources are
 * resolved from {tiers|plans|items} so swapping variants preserves the
 * editable surface area.
 * ════════════════════════════════════════════════════════════════════════ */
export function ServicesExt({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const layout = block.layout;
  const bid = block.id;
  const inlineTid = (f) => `inline-${bid}-${f}`;

  // Resolve the source array name so dotted-path inline edits write back
  // to the exact field the side panel knows about.
  const sourceField =
    (Array.isArray(c.tiers) && c.tiers.length) ? 'tiers' :
    (Array.isArray(c.plans) && c.plans.length) ? 'plans' :
    (Array.isArray(c.items) && c.items.length) ? 'items' :
    'tiers';
  const rawTiers = c[sourceField] || [
    { name:'Starter', price:'$0', features:['Feature A','Feature B'] },
    { name:'Pro', price:'$19', features:['Everything in Starter','Feature C','Feature D'], highlighted:true },
    { name:'Master', price:'$49', features:['Everything in Pro','Feature E','Feature F'] },
  ];
  const tiers = rawTiers.map(t => ({
    name:        t.name        || t.title       || '',
    price:       t.price       || '',
    features:    t.features    || [],
    description: t.description || '',
    highlighted: !!t.highlighted,
    cta_label:   t.cta_label   || '',
  }));
  const pricePeriod = c.price_period || '/mo';

  if (layout === 'pricing-tabs') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Simple, Flexible Pricing'} tag="h2" className="text-3xl md:text-4xl font-bold text-center" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || ''} tag="p" className="text-center mt-3 opacity-70 max-w-xl mx-auto" placeholder="Tagline" />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.slice(0,3).map((tier,i)=>(
              <div key={i} className={`p-8 rounded-2xl border-2 ${tier.highlighted?'border-violet-500 shadow-2xl scale-105':'border-gray-200'}`} style={{ borderRadius: theme.radius || 16 }}>
                <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={tier.name} tag="p" className="text-xs uppercase tracking-wider font-bold opacity-70" placeholder="Tier" />
                <p data-testid={inlineTid(`tier_${i}_price`)} className="mt-3 text-4xl font-bold" style={{ color: tier.highlighted ? theme.accent : theme.ink }}>
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.price`} value={tier.price} tag="span" placeholder="$0" />
                  <span className="text-base font-normal opacity-60">{pricePeriod}</span>
                </p>
                <ul className="mt-6 space-y-2.5 text-sm">{(tier.features || []).map((f,fi)=>(
                  <li key={fi} className="flex gap-2"><span style={{ color: theme.accent }}>✓</span>
                    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.features.${fi}`} value={f} tag="span" placeholder="Feature" />
                  </li>
                ))}</ul>
                <span
                  data-testid={inlineTid(`tier_${i}_cta`)}
                  {...getWbButtonAttrs(i, sourceField)}
                  className={`${getWbButtonAttrs(i, sourceField).className} mt-8 block text-center w-full py-3 rounded-full text-xs font-bold uppercase tracking-wider ${tier.highlighted?'text-white':'border border-gray-300'} cursor-text`}
                  style={{ background: tier.highlighted ? theme.accent : 'transparent', color: tier.highlighted ? '#fff' : theme.ink }}
                >
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.cta_label`} value={tier.cta_label || c.cta_label || 'Get Started'} tag="span" placeholder="Button" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'pricing-comparison-grid') {
    const features = c.features || ['Feature 1','Feature 2','Feature 3','Feature 4'];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Compare Plans'} tag="h2" className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2" style={{ borderColor: theme.accent }}>
                  <th className="py-3 text-xs uppercase tracking-wider opacity-60">Feature</th>
                  {tiers.map((t,i)=>(
                    <th key={i} className="py-3 text-center font-bold">
                      <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={t.name} tag="span" placeholder="Tier" />
                      <div className="text-xs font-normal opacity-60 mt-1">
                        <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.price`} value={t.price} tag="span" placeholder="$0" />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((f,fi)=>(
                  <tr key={fi} className="border-b border-gray-100">
                    <td className="py-3 text-sm">
                      <E block={block} onEdit={onEdit} field={`features.${fi}`} value={f} tag="span" placeholder="Feature" />
                    </td>
                    {tiers.map((t,ti)=><td key={ti} className="text-center text-sm">{fi < (t.features?.length || 0) ? <span style={{ color: theme.accent }}>✓</span> : <span className="opacity-30">—</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'pricing-dark-hero') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative px-6 py-24 overflow-hidden" style={{ background: '#0E0E23' }}>
        {c.image_url && <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="absolute inset-0 bg-gradient-to-b from-violet-900/40 via-transparent to-black/60" />
        <div className="relative max-w-6xl mx-auto text-white text-center">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Pick your plan'} tag="h2" className="text-3xl md:text-5xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || ''} tag="p" className="mt-4 opacity-80 max-w-xl mx-auto" placeholder="Tagline" />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.slice(0,3).map((tier,i)=>(
              <div key={i} className={`p-8 rounded-2xl border ${tier.highlighted ? 'border-pink-400/60 bg-white/10' : 'border-white/15 bg-white/5'}`} style={{ borderRadius: theme.radius || 16 }}>
                <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={tier.name} tag="p" className="text-xs uppercase tracking-wider opacity-70 font-bold" placeholder="Tier" />
                <p data-testid={inlineTid(`tier_${i}_price`)} className="mt-3 text-4xl font-bold">
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.price`} value={tier.price} tag="span" placeholder="$0" />
                  <span className="text-base font-normal opacity-60">{pricePeriod}</span>
                </p>
                <ul className="mt-6 space-y-2 text-sm opacity-90 text-left">{(tier.features || []).map((f,fi) => (
                  <li key={fi}>· <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.features.${fi}`} value={f} tag="span" placeholder="Feature" /></li>
                ))}</ul>
                <span
                  data-testid={inlineTid(`tier_${i}_cta`)}
                  {...getWbButtonAttrs(i, sourceField)}
                  className={`${getWbButtonAttrs(i, sourceField).className} mt-8 block text-center w-full py-3 rounded-full text-xs font-bold uppercase tracking-wider text-white cursor-text`}
                  style={{ background: tier.highlighted ? '#EC4899' : 'rgba(255,255,255,0.12)' }}
                >
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.cta_label`} value={tier.cta_label || c.cta_label || 'Get Started'} tag="span" placeholder="Button" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'pricing-highlighted-middle') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Simple, flexible pricing'} tag="h2" className="text-3xl md:text-4xl font-bold text-center" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-0 items-stretch">
            {tiers.slice(0,3).map((tier,i)=>{
              const isMid = i === 1 || tier.highlighted;
              return (
                <div key={i} className={`p-8 ${isMid ? 'rounded-2xl text-white shadow-2xl z-10 -my-4 md:scale-105' : 'border border-current/10'}`}
                     style={isMid ? { background: theme.accent || '#7C3AED', borderRadius: theme.radius || 16 } : { borderRadius: theme.radius || 16 }}>
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={tier.name} tag="p" className={`text-xs uppercase tracking-wider font-bold ${isMid ? 'opacity-80' : 'opacity-60'}`} placeholder="Tier" />
                  <p data-testid={inlineTid(`tier_${i}_price`)} className="mt-3 text-4xl font-bold">
                    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.price`} value={tier.price} tag="span" placeholder="$0" />
                    <span className="text-base font-normal opacity-60">{pricePeriod}</span>
                  </p>
                  <ul className={`mt-6 space-y-2 text-sm ${isMid ? 'opacity-95' : 'opacity-80'}`}>{(tier.features || []).map((f,fi) => (
                    <li key={fi} className="flex gap-2"><span>✓</span>
                      <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.features.${fi}`} value={f} tag="span" placeholder="Feature" />
                    </li>
                  ))}</ul>
                  <span
                    data-testid={inlineTid(`tier_${i}_cta`)}
                    {...getWbButtonAttrs(i, sourceField)}
                    className={`${getWbButtonAttrs(i, sourceField).className} mt-8 block text-center w-full py-3 rounded-full text-xs font-bold uppercase tracking-wider ${isMid ? 'bg-white text-gray-900' : 'border border-current/30'} cursor-text`}
                  >
                    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.cta_label`} value={tier.cta_label || c.cta_label || 'Get Started'} tag="span" placeholder="Button" />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'pricing-membership') {
    const imgs = c.images || c.gallery || [];
    const slotFor = (i) => imgs[i] || `https://picsum.photos/seed/${bid}-membership-${i}/600/600`;
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-2">
            {[0,1,2,3].map((i) => (
              <div key={i} className="aspect-square overflow-hidden rounded-lg">
                <SafeImg src={slotFor(i)} field={`image_${i}`} blockId={bid} className="w-full h-full object-cover cursor-pointer" alt="" />
              </div>
            ))}
          </div>
          <div>
            <E block={block} onEdit={onEdit} field="title" value={c.title || 'Membership'} tag="h2" className="text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
            <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || ''} tag="p" className="mt-4 opacity-75" placeholder="Tagline" />
            <div className="mt-8 space-y-3">
              {tiers.slice(0,2).map((tier,i) => (
                <div key={i} className="flex justify-between items-center border-b border-current/10 py-3">
                  <div>
                    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={tier.name} tag="p" className="font-bold" placeholder="Tier" />
                    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.description`} value={tier.description || 'Includes everything'} tag="p" className="text-xs opacity-60" placeholder="Description" />
                  </div>
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.price`} value={tier.price} tag="p" className="text-2xl font-bold" style={{ color: theme.accent }} placeholder="$0" />
                </div>
              ))}
            </div>
            <span
              data-testid={inlineTid('cta')}
              {...getWbButtonAttrs(0, c.buttons && c.buttons.length ? 'buttons' : 'cta_label')}
              className={`${getWbButtonAttrs(0, c.buttons && c.buttons.length ? 'buttons' : 'cta_label').className} mt-6 inline-block px-8 py-3 rounded-full text-sm font-bold uppercase tracking-wider text-white cursor-text`}
              style={{ background: '#EC4899' }}
            >
              <E block={block} onEdit={onEdit}
                 field={c.buttons && c.buttons.length ? 'buttons.0.text' : 'cta_label'}
                 value={(c.buttons && c.buttons[0]?.text) || c.cta_label || 'Become a member'}
                 tag="span" placeholder="Button" />
            </span>
          </div>
        </div>
      </section>
    );
  }

  // ── Iter 189c Phase B · Pricing — NEW `pricing-with-testimonial` ──
  // 3-tier row + a customer quote card below the middle (highlighted) plan.
  // Trust signal at the exact moment of purchase decision.
  if (layout === 'pricing-with-testimonial') {
    const q = c.testimonial || { quote: '"Pro paid for itself in a week. My whole team is on it now."', author: 'Sam Rivera', role: 'Head of Design, Kindred', image_url: '' };
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto text-center mb-14">
          <Eyebrow theme={theme}>{c.eyebrow || 'PRICING'}</Eyebrow>
          <H1 theme={theme}><E block={block} field="title" value={c.title || 'Pick the plan that fits.'} tag="span" placeholder="Pricing" /></H1>
          {c.tagline && <E block={block} onEdit={onEdit} field="tagline" value={c.tagline} tag="p" className="mt-4 text-lg opacity-75 max-w-xl mx-auto" placeholder="Tagline" />}
        </div>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((t, i) => (
            <div key={i} className="p-8 flex flex-col" style={{
              background: t.highlighted ? theme.ink : theme.bg,
              color: t.highlighted ? theme.bg : theme.ink,
              border: t.highlighted ? 'none' : `1px solid ${theme.ink}18`,
              borderRadius: theme.radius || 12,
              transform: t.highlighted ? 'translateY(-10px)' : 'none',
              boxShadow: t.highlighted ? '0 20px 60px -20px rgba(0,0,0,0.35)' : 'none',
            }}>
              {t.highlighted && <p className="inline-block self-start px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.2em] font-bold mb-4" style={{ background: theme.accent, color: '#fff' }}>Most popular</p>}
              <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={t.name} tag="h3" className="text-lg font-bold" placeholder="Plan" />
              <div className="mt-3 flex items-baseline gap-1">
                <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.price`} value={t.price} tag="span" className="text-5xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="$—" />
                <span className="opacity-60 text-sm">{pricePeriod}</span>
              </div>
              <ul className="mt-6 space-y-2 flex-1">
                {t.features.map((f, fi) => (
                  <li key={fi} className="flex items-start gap-2 text-sm">
                    <span className="mt-1 shrink-0 opacity-70">✓</span>
                    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.features.${fi}`} value={f} tag="span" placeholder="Feature" />
                  </li>
                ))}
              </ul>
              <a href="#" {...getWbButtonAttrs(block, 'primary')} className="mt-6 py-2.5 rounded-full text-center font-bold text-sm uppercase tracking-wider" style={{ background: t.highlighted ? theme.bg : theme.ink, color: t.highlighted ? theme.ink : theme.bg }}>
                {t.cta_label || 'Select'}
              </a>
            </div>
          ))}
        </div>
        <div className="max-w-3xl mx-auto mt-16 relative">
          <div className="absolute left-1/2 -top-4 -translate-x-1/2 w-8 h-8 rotate-45" style={{ background: theme.accent + '10', border: `1px solid ${theme.accent}30`, borderRight: 'none', borderBottom: 'none' }} />
          <div className="p-8 text-center" style={{ background: theme.accent + '10', border: `1px solid ${theme.accent}30`, borderRadius: theme.radius || 12 }}>
            <div className="flex justify-center gap-1 mb-4">
              {[0,1,2,3,4].map(i => <svg key={i} width="14" height="14" viewBox="0 0 12 12"><path d="M6 0l1.7 4 4.3.3-3.3 2.7 1 4.2L6 9l-3.7 2.2 1-4.2L0 4.3l4.3-.3z" fill="#F59E0B"/></svg>)}
            </div>
            <E block={block} onEdit={onEdit} field="testimonial.quote" value={q.quote} tag="blockquote" className="text-xl italic leading-relaxed" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: theme.ink }} placeholder="Customer quote" />
            <div className="mt-5 flex items-center justify-center gap-3">
              {q.image_url && <SafeImg src={q.image_url} alt="" className="w-10 h-10 rounded-full object-cover" />}
              <div className="text-left">
                <E block={block} onEdit={onEdit} field="testimonial.author" value={q.author} tag="p" className="text-sm font-bold" placeholder="Author" />
                <E block={block} onEdit={onEdit} field="testimonial.role" value={q.role} tag="p" className="text-xs opacity-60" placeholder="Role, Company" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── Iter 189c Phase B · Pricing — NEW `pricing-annual-toggle-visual` ──
  // 3-tier layout with a prominent monthly/yearly toggle at top + visual
  // "Save $X/yr" callout. Both prices shown per-tier; toggle simulates
  // via `--wb-billing` CSS var (in a real page state hook would swap).
  if (layout === 'pricing-annual-toggle-visual') {
    const period = c.default_period || 'monthly';
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto text-center mb-12">
          <Eyebrow theme={theme}>{c.eyebrow || 'PRICING'}</Eyebrow>
          <H1 theme={theme}><E block={block} field="title" value={c.title || 'One price. All the features.'} tag="span" placeholder="Pricing title" /></H1>
          <div className="mt-8 inline-flex items-center gap-2 p-1 rounded-full" style={{ background: theme.ink + '10', border: `1px solid ${theme.ink}18` }} data-testid={`inline-${bid}-toggle`}>
            <button type="button" className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${period === 'monthly' ? '' : 'opacity-60'}`} style={{ background: period === 'monthly' ? theme.ink : 'transparent', color: period === 'monthly' ? theme.bg : theme.ink }}>Monthly</button>
            <button type="button" className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all inline-flex items-center gap-2 ${period === 'yearly' ? '' : 'opacity-60'}`} style={{ background: period === 'yearly' ? theme.ink : 'transparent', color: period === 'yearly' ? theme.bg : theme.ink }}>
              Yearly
              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold" style={{ background: theme.accent, color: '#fff' }}>{c.savings_pill || 'Save 20%'}</span>
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          <div className="hidden md:block absolute -top-3 left-1/2 -translate-x-1/2 z-10">
            <div className="relative" style={{ transform: 'rotate(-6deg)' }}>
              <div className="px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: theme.accent, color: '#fff' }}>{c.middle_savings_text || 'Save $60/year'}</div>
              <svg className="absolute -bottom-4 -left-6" width="30" height="24" viewBox="0 0 30 24" fill="none">
                <path d="M2 2 C 10 4, 18 10, 22 20" stroke={theme.accent} strokeWidth="1.6" strokeLinecap="round" fill="none" strokeDasharray="2 2" />
                <path d="M18 14 L 22 20 L 26 14" stroke={theme.accent} strokeWidth="1.6" strokeLinecap="round" fill="none" />
              </svg>
            </div>
          </div>
          {tiers.map((t, i) => (
            <div key={i} className="p-8 flex flex-col" style={{
              background: t.highlighted ? theme.ink : theme.bg,
              color: t.highlighted ? theme.bg : theme.ink,
              border: t.highlighted ? 'none' : `1px solid ${theme.ink}18`,
              borderRadius: theme.radius || 12,
            }}>
              <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={t.name} tag="h3" className="text-lg font-bold" placeholder="Plan" />
              <div className="mt-3 flex items-baseline gap-1">
                <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.price`} value={t.price} tag="span" className="text-5xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="$—" />
                <span className="opacity-60 text-sm">/mo</span>
              </div>
              {t.description && <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.description`} value={t.description} tag="p" className="mt-2 text-sm opacity-70" placeholder="Description" />}
              <ul className="mt-6 space-y-2 flex-1">
                {t.features.map((f, fi) => (
                  <li key={fi} className="flex items-start gap-2 text-sm">
                    <span className="mt-1 shrink-0" style={{ color: theme.accent }}>✓</span>
                    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.features.${fi}`} value={f} tag="span" placeholder="Feature" />
                  </li>
                ))}
              </ul>
              <a href="#" className="mt-6 py-2.5 rounded-full text-center font-bold text-sm uppercase tracking-wider" style={{ background: t.highlighted ? theme.bg : theme.ink, color: t.highlighted ? theme.ink : theme.bg }}>
                {t.cta_label || 'Get started'}
              </a>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return null;
}
